import { useRef, useState } from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { gross, isPro, lei, packTotals, shortName, treatSets } from "@/lib/pricing";
import { waLink } from "@/lib/wa";
import { WhatsAppGlyph } from "@/components/cards/CardShell";

const Stepper = ({ value, onChange, id }) => (
  <div className="pc-step">
    <button type="button" onClick={() => onChange(Math.max(0, value - 1))} aria-label="Minus" data-testid={`calc-minus-${id}`}><Minus size={14} /></button>
    <input
      inputMode="numeric"
      value={value}
      onChange={(e) => onChange(Math.min(999, Math.max(0, parseInt(e.target.value.replace(/\D/g, ""), 10) || 0)))}
      data-testid={`calc-qty-${id}`}
    />
    <button type="button" onClick={() => onChange(Math.min(999, value + 1))} aria-label="Plus" data-testid={`calc-plus-${id}`}><Plus size={14} /></button>
  </div>
);

export default function ProfitCalculator({ data, track }) {
  const [offer, setOffer] = useState(() => data.packages.find((pk) => pk.id === (data.offer?.pack_id || new URLSearchParams(window.location.search).get("oferta"))) || null);
  const [qty, setQty] = useState(() => (offer ? Object.fromEntries(offer.items.map((it) => [it.id, it.qty])) : {}));
  const [svcText, setSvcText] = useState(String(data.service_price));
  const svc = parseInt(svcText, 10) || 0;
  const seen = useRef(new Set());
  const setOne = (id, v) => {
    if (v > 0 && !seen.current.has(id)) {
      seen.current.add(id);
      track?.("product", id);
    }
    setOffer(null);
    setQty((q) => ({ ...q, [id]: v }));
  };
  const m = data.markup / 100;
  const items = data.products.filter((p) => qty[p.id]);
  const g = (p) => gross(p.price, data.vat);
  const ot = offer && packTotals(offer, { ...data, service_price: svc });
  const listCost = items.reduce((s, p) => s + g(p) * qty[p.id], 0);
  const cost = ot ? ot.price : listCost;
  const sets = treatSets(data, data.products.map((p) => ({ id: p.id, qty: qty[p.id] || 0 })));
  const treatRevenue = ot ? ot.treatRevenue : sets * data.treatments * svc;
  const resaleSum = ot ? ot.resale : Math.round(items.filter((p) => !isPro(data, p.id)).reduce((s, p) => s + g(p) * qty[p.id], 0) * (1 + m));
  const revenue = treatRevenue + resaleSum;
  const profit = revenue - cost;
  const pieces = items.reduce((s, p) => s + qty[p.id], 0);
  const loosePro = items.some((p) => isPro(data, p.id) && qty[p.id] > sets);
  const who = data.offer ? ` (${data.offer.salon})` : "";
  const head = offer ? `Bună ziua${who}, aș dori să comand ${offer.name} (-${offer.discount}%):` : `Bună ziua${who}, aș dori să comand:`;
  const byId = Object.fromEntries(data.products.map((p) => [p.id, p]));
  const gifts = offer?.gifts?.length ? `\nCadou: ${offer.gifts.filter((it) => byId[it.id]).map((it) => `${it.qty}× ${shortName(byId[it.id].name)} ${byId[it.id].size}`).join(", ")}` : "";
  const text = `${head}\n${items.map((p) => `• ${qty[p.id]}× ${shortName(p.name)} ${p.size} (cod ${p.code})`).join("\n")}${gifts}\nTotal: ${lei(cost)} (TVA inclus)`;
  const wa = waLink(data.contact.whatsapp, text);

  return (
    <section className="pc-box" data-testid="profit-calculator">
      <div className="pc-head">
        <h3 className="pub-sub">Calculator profit</h3>
        {pieces > 0 && (
          <button type="button" className="pc-reset" onClick={() => { setQty({}); setOffer(null); }} data-testid="calc-reset-btn"><RotateCcw size={12} /> Resetează</button>
        )}
      </div>
      {offer && (
        <div className="pc-offer" data-testid="calc-offer-banner">
          <small>{data.offer ? `Ofertă pentru ${data.offer.salon}` : "Ofertă pregătită pentru tine"}</small>
          <b>{offer.name} · -{offer.discount}%</b>
          {offer.gifts?.length > 0 && <span>+ {offer.gifts.reduce((s, it) => s + it.qty, 0)} produse cadou incluse în calcul</span>}
        </div>
      )}
      <p className="pf-note">Alege câte bucăți vrei din fiecare produs și vezi imediat cât câștigi.</p>
      <label className="pc-svc" data-testid="calc-service-price">
        <span>
          <b>Prețul tău pe tratament</b>
          <small>Minim recomandat {lei(data.service_price)} · {data.treatments} tratamente / set</small>
        </span>
        <span className="pc-svc-input">
          <input
            inputMode="numeric"
            value={svcText}
            onChange={(e) => setSvcText(e.target.value.replace(/\D/g, "").slice(0, 6))}
            data-testid="calc-service-price-input"
          />
          lei
        </span>
      </label>
      {svcText !== "" && svc < data.service_price && <p className="pf-note pc-warn" data-testid="calc-service-warning">Atenție: sub prețul minim recomandat de {lei(data.service_price)}.</p>}
      <ul className="pc-list">
        {data.products.map((p) => (
          <li key={p.id} className={qty[p.id] ? "is-on" : ""}>
            <div className="pl-info">
              <b>{shortName(p.name)} <span className="pl-size">{p.size}</span></b>
              <span className="pl-idx">{lei(g(p))} · {isPro(data, p.id) ? "uz salon" : <span className="pl-resale">profit {lei(g(p) * m)}/buc</span>}</span>
            </div>
            <Stepper id={p.id} value={qty[p.id] || 0} onChange={(v) => setOne(p.id, v)} />
          </li>
        ))}
      </ul>
      <div className="pc-total" data-testid="calc-total">
        <div><span>{pieces} bucăți · investiție</span><b data-testid="calc-cost">{lei(cost)}</b><small>{offer ? `preț pachet, discount ${offer.discount}% · TVA inclus` : "TVA inclus"}</small></div>
        <div><span>Încasezi</span><b data-testid="calc-revenue">{lei(revenue)}</b><small>{ot ? ot.treatments : sets * data.treatments} tratamente {lei(treatRevenue)} + <em className="pl-resale">revânzare {lei(resaleSum)}</em></small></div>
        <div className="is-main"><span>Profitul tău</span><b data-testid="calc-profit">{lei(profit)}</b><small>randament {cost ? Math.round((profit / cost) * 100) : 0}%</small></div>
      </div>
      {loosePro && <p className="pf-note" data-testid="calc-set-note">Tratamentele se calculează la seturi complete: câte un Pre-Shampoo 400 ml, Shampoo 500 ml și Conditioner 400 ml = minim {data.treatments} tratamente.</p>}
      {pieces > 0 && wa && (
        <a className="st-btn pub-wa pc-order" href={wa} target="_blank" rel="noopener noreferrer" onClick={() => track?.("order", offer ? "offer" : "calc", items.map((p) => ({ id: p.id, qty: qty[p.id] })))} data-testid="calc-order-btn">
          <WhatsAppGlyph /> Trimite comanda pe WhatsApp
        </a>
      )}
      <p className="pf-note">Pentru randament mai mare, vezi pachetele din „Oferte active”.</p>
    </section>
  );
}
